/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { getCatalogId, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
import {
    CommerceProperty, ProductCatalog,
    ProductDeliveryOptions,
    ProductDimensionValue,
    //ProductDimensionValueInventoryAvailability,
    ProductPrice,
    ReleasedProductType,
    SimpleProduct,
    UnitOfMeasureConversion,
    format
} from '@msdyn365-commerce/retail-proxy';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions,
    DeliveryMode,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant,
    GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getDimensionValuesFromQuery,
    //getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant,
    getProductAvailabilitiesForSelectedVariant,
    getSelectedVariant,
    //IDimensionsApp,
    IDimensionValueForSelectedVariant,
    //InventoryLevelValues,
    IProductInventoryInformation,
    IPromiseQueue,
    PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput,
    SelectedVariantInput,
    setDimensionValuesToQuery,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    updateMaxQuantityForCartLineItem
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    //getBuyboxAddToCart,
    //getBuyBoxInventoryLabel,
    getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate,
    getBuyboxProductAddToWishlist,
    getBuyboxProductComparisonButton,
    //getBuyboxProductPrice,
    getBuyboxProductQuantity,
    getBuyboxProductRating,
    getBuyboxProductTitle,
    getBuyboxProductUnitOfMeasure,
    getBuyboxShopSimilarButton,
    getQuantityLimitsMessages,
    RetailDefaultOrderQuantityLimitsFeatureName,
    EcommerceQuantityLimitConfigurationsFeatureName,
    RevertToSiteBuilderOrderQuantityLimitsSettingsCRTFeatureName
} from '@msdyn365-commerce-modules/buybox';
import { getBuyboxProductConfigure } from '@msdyn365-commerce-modules/buybox';
import { getBuyboxAddToCart, getBuyboxProductPrice, IBuyboxExtentedProps, IBuyboxState } from './common';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxCommonData,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IErrorState,
    ShopSimiliarButtonType
} from '@msdyn365-commerce-modules/buybox';
import { IVinhusetBuyboxData } from './vinhuset-buybox.data';
import { IVinhusetBuyboxProps, IVinhusetBuyboxResources } from './vinhuset-buybox.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/vinhuset-buybox-find-in-store';
import { RecentlyViewedState } from '@msdyn365-commerce-modules/buybox';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { getAttributeStringOrDefault } from '../../shared-utils/attributes';
import { getLocale } from '../../shared-utils/attributes/locale';
import { getUrlSync } from '@msdyn365-commerce/core';
import { getDiscountLines } from '../../themes/vinhuset-adventureworks/views/components/price.component';
import { addInventoryVisibilityInfo } from '../../shared-utils/string-utils';
import { humanizeUnitOfMeasure } from '../../components/unit-of-measure';
import { LifeCycleStatus } from '../../shared-utils/types';

export interface IBuyboxViewProps extends IVinhusetBuyboxProps<IVinhusetBuyboxData> {
    state: IBuyboxState;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IBuyboxCallbacks;
    mediaGallery?: React.ReactNode;
    category?: React.ReactNode;
    producer?: React.ReactNode;
    title?: React.ReactNode;
    region?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    literPrice?: React.ReactNode;
    deposit?: React.ReactNode;
    volumeCl?: React.ReactNode;
    pricePer?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    min?: number | undefined;
    max: number | undefined;
    addToCart: IBuyboxAddToCartViewProps;
    findInStore?: IBuyboxFindInStoreViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    catalogs?: ProductCatalog[] | undefined;
    isChannelMultipleCatalogsFeatureEnabled?: boolean;
    currentCatalogId?: number;
    applyDefaultOrderSettings?: boolean;
    unitConversions: UnitOfMeasureConversion[];
    onBreakoffChange: (breakoff: boolean) => void;
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<IVinhusetBuyboxProps<IVinhusetBuyboxData>, IBuyboxState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};
    private initializedDimensions: Set<string> = new Set();

    private readonly buyboxCallbacks: IBuyboxCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
        initializeDimension: (initializedDimension: string): void => {
            if (initializedDimension) {
                this.initializedDimensions.add(initializedDimension);
            }
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[selectedDimensionId] = selectedDimensionValueId;
            // Only trigger _updateDimensions when all dimensions are initialized
            if (this.initializedDimensions.size === (this.props.data.product.result?.Dimensions?.length || 0)) {
                return this.dimensionUpdateQueue
                    .enqueue(async () => {
                        return this._updateDimensions();
                    })
                    .catch((error: unknown) => {
                        // Ignore discarded processes.
                        if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                            throw error;
                        }
                    });
            }
        },
        getDropdownName: (dimensionType: number, resources: IVinhusetBuyboxResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        }
    };

    private readonly telemetryContent: ITelemetryContent;

    private readonly BuyAlwaysAdd = true;

    private readonly attributeLocale = getLocale(this.props.context);

    public constructor(props: IVinhusetBuyboxProps<IVinhusetBuyboxData>, state: IBuyboxState) {
        super(props);

        const { data: { cart, product }} = props;
        const cartQuantity = cart.result?.cart.CartLines?.find(x => x.ProductId === product.result?.RecordId)?.Quantity;

        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: this.BuyAlwaysAdd ? 0 : cartQuantity || 0,
            breakoff: true,
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: props.data.productPrice.result,
            productDeliveryOptions: undefined,

            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false,
            isPriceApiCalled: false,
            unitConversions: []
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPrice
            },
            context
        } = this.props;

        // There are multiple sources triggering the callback function dimensionSelectedAsync calling the GetActivePrice api before componentDidMount and after componentDidMount.
        // Set isPriceApiCalled to false here to prevent redundant API call after componentDidMount:
        // If the price has not been hydrated before, this means the component was just loaded for the first time with variant selected.
        // We will skip calling RS API in this case as the price data has already been fetched via module data action in node server.

        const matchedDimensions = getDimensionValuesFromQuery(this.props.context.request.url.requestUrl);

        // Set isPriceApiCalled to false only when variant is there
        if (matchedDimensions.length > 0) {
            this.setState({ isPriceApiCalled: false });
        } else {
            this.setState({ isPriceApiCalled: true });
        }

        void productPrice.then(async result => {
            this._updatePrice(result);
        });
        void this._getUnitConversions();

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
        }

        if (product && context.app.config.maxRecentlyViewedItemsCount > 0) {
            RecentlyViewedState.instance(this.props.context, context.app.config.maxRecentlyViewedItemsCount, product.RecordId);
        }
    }

    private _getClassNamePrefix(typeName: string): string {
        return typeName.toLocaleLowerCase() === 'quickview' ? 'ms-quickView' : 'ms-buybox';
    }

    private _getBuyBoxInventoryLabel(props: IBuyboxExtentedProps<IBuyboxCommonData>): React.ReactElement | undefined {

        const { context, typeName, resources } = props;

        // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const productAvailableQuantity = props.data.productAvailableQuantity?.result ?? undefined;

        const className = this._getClassNamePrefix(typeName);
        if (!productAvailableQuantity || !ArrayExtensions.hasElements(productAvailableQuantity)) {
            return undefined;
        }
        const shippingInventory = productAvailableQuantity.find(shipping => shipping.deliveryType === DeliveryMode.shipping);

        if (shippingInventory) {
            let inventoryClassName: string;
            if (shippingInventory.StockLevelCode) {
                inventoryClassName = `${className}__inventory-label ${className}__inventory-code-${shippingInventory.StockLevelCode.toLowerCase()}`;
            } else {
                inventoryClassName = `${className}__inventory-label`;
            }

            let availabilityClassName = 'retrieving';

            if (shippingInventory.IsProductAvailable) {
                availabilityClassName = 'available';
            } else if (shippingInventory.IsProductAvailable === false) {
                availabilityClassName = 'not-available';
            }

            if (shippingInventory?.StockLevelCode === 'ERR') {
                availabilityClassName = 'error';
            }

            let stockLevelLabel: string | JSX.Element | undefined = shippingInventory.StockLevelLabel;

            if (props.context.request.locale !== 'en-US') {
                if (stockLevelLabel === 'Available') {
                    stockLevelLabel = 'På lager';
                } else if (stockLevelLabel === 'Out of stock') {
                    stockLevelLabel = <>&nbsp;</>;
                }
            }

            if (typeof stockLevelLabel === 'string') {
                const availableQuantity = shippingInventory.ProductAvailableQuantity.AvailableQuantity || 0;
                stockLevelLabel = format(stockLevelLabel, availableQuantity);
                stockLevelLabel = addInventoryVisibilityInfo(
                    stockLevelLabel,
                    shippingInventory,
                    resources.expectedInventoryDateWithQuantityText,
                    resources.expectedInventoryDateText);
            }

            const soldOutUntilDate = this.props.data.productSpecificationData.result?.find(x => x.Name === this.attributeLocale.soldOutUntilDate)?.DateTimeOffsetValue;
            const soldOutUnknownDate = this.props.data.productSpecificationData.result?.find(x => x.Name === this.attributeLocale.lifeCycle)?.TextValue
                ?.split('-')[0]
                ?.trim() === LifeCycleStatus.SoldOutUnknownDate;

            if (!shippingInventory.IsProductAvailable) {
                if (soldOutUntilDate && new Date() < new Date(soldOutUntilDate)) {
                    stockLevelLabel = format(resources.soldOutUntilDateText, new Date(soldOutUntilDate)
                        .toLocaleDateString(context.request.locale, { day: '2-digit', month: '2-digit', year: 'numeric' }));
                } else if (soldOutUnknownDate) {
                    stockLevelLabel = resources.soldOutUnknownDateText;
                }
            }

            return (
                <div className={`${className}__inventory-info`}>
                    <div className={`${className}__inventory-info__prefix`}>Status:</div>
                    <div className={classnames(inventoryClassName, availabilityClassName)}>{stockLevelLabel}</div>
                </div>
            );
        }

        return undefined;
    }

    public render(): JSX.Element | null {
        const {
            slots: { mediaGallery, productComparisonButton },
            data: {
                product: { result: product }
            },
            config: { className = '' }
        } = this.props;

        const { min, max } = this.state;

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }

        const props = this.props as IBuyboxExtentedProps<IVinhusetBuyboxData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        // const context = this.props.context as ICoreContext<IDimensionsApp>;
        // const inventoryLevel = context.app.config.inventoryLevel;
        // const dimensionValuesWithInventory = ArrayExtensions.flatten(
        //     ArrayExtensions.validValues(this.props.data.productDimensions.result?.map(value => value.dimensionValuesWithInventory))
        // );
        const applyDefaultOrderSettings =
            (this.props.data.featureState?.result?.find(featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName)
                ?.IsEnabled ??
                false) &&
            (this.props.data.cartConfiguration?.result?.ValidateDefaultOrderSettingsPerLine ?? false);
        const hasAvailableProducts = true;
        const catalogId = getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);

        const viewProps: IBuyboxViewProps = {
            ...(this.props as IVinhusetBuyboxProps<IVinhusetBuyboxData>),
            state: this.state,
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('ms-buybox', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery'
            },
            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            category: this._renderProductCategory(),
            producer: this._renderProductProducer(),
            title: getBuyboxProductTitle(props),
            region: this._renderProductRegion(),
            description: this._renderProductDescription(),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            unitOfMeasure: getBuyboxProductUnitOfMeasure(props),
            configure: getBuyboxProductConfigure(props, this.state, this.buyboxCallbacks),
            findInStore: getBuyboxFindInStore(this.props, this.state, this.buyboxCallbacks),
            price: getBuyboxProductPrice(props, this.state),
            literPrice: this._renderLiterPrice(),
            deposit: this._renderDepositPrice(),
            volumeCl: this._renderVolumeCl(),
            pricePer: this._renderPricePer(),
            addToCart: getBuyboxAddToCart(
                props,
                this.state,
                this.attributeLocale,
                this.buyboxCallbacks,
                defaultMinimumKeyInPrice,
                defaultMaximumKeyInPrice,
                undefined,
                hasAvailableProducts,
                (quantity: number) => this.setState({ quantity })
            ),
            productComparisonButton: ArrayExtensions.hasElements(productComparisonButton)
                ? getBuyboxProductComparisonButton(
                      productComparisonButton[0],
                      product,
                      getCatalogId(this.props.context.request),
                      this.state
                  )
                : undefined,
            addToOrderTemplate: getBuyboxProductAddToOrderTemplate(
                props,
                this.state,
                this.buyboxCallbacks,
                this._isChannelMultipleCatalogsFeatureEnabled(),
                catalogId
            ),
            addToWishlist: getBuyboxProductAddToWishlist(props, this.state, this.buyboxCallbacks),
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(props),
            quantity: product.IsGiftCard ? undefined : getBuyboxProductQuantity(props, this.state, this.buyboxCallbacks),
            inventoryLabel: this._getBuyBoxInventoryLabel(props),
            shopSimilarLook:
                this.props.config.enableShopSimilarLooks && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Looks)
                    : undefined,
            shopSimilarDescription:
                this.props.config.enableShopSimilarDescription && !product.IsGiftCard
                    ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description)
                    : undefined,
            keyInPrice:
                this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected
                    ? getBuyboxKeyInPrice(props, this.state, this.buyboxCallbacks)
                    : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(props, this.state),
            min: applyDefaultOrderSettings ? min : 1,
            max,
            applyDefaultOrderSettings: applyDefaultOrderSettings,
            isChannelMultipleCatalogsFeatureEnabled: this._isChannelMultipleCatalogsFeatureEnabled(),
            currentCatalogId: catalogId,
            catalogs: this._validateCatalogDetails(catalogId),
            unitConversions: this.state.unitConversions,
            onBreakoffChange: (breakoff: boolean) => this.setState({ breakoff })
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        const scrollX = boundingRect.left + window.pageXOffset;
        const scrollY = boundingRect.top + window.pageYOffset;

        window.scrollTo(scrollX, scrollY);
    };
    private readonly _isChannelMultipleCatalogsFeatureEnabled = (): boolean => {
        let isMultipleCatalogFeatureEnabledInHq = false;
        if (this.props.data.featureState) {
            isMultipleCatalogFeatureEnabledInHq =
                this.props.data.featureState.result?.find(
                    feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                )?.IsEnabled || false;
        }
        return isMultipleCatalogFeatureEnabledInHq;
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }

    private _updatePrice(newPrice: ProductPrice | undefined, customPrice: number | undefined = this.state.keyInPriceAmount): void {
        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }

        // Get first discount that will expire and show that under discount price
        const discounts = getDiscountLines(newPrice?.DiscountLines);
        const firstExpiringDiscount = discounts.find(x => !!x.OfferId && !!x.OfferName && !!x.ValidTo)?.ValidTo;

        this.setState({
            productPrice: newPrice,
            firstExpiringDiscount: firstExpiringDiscount ? new Date(firstExpiringDiscount) : undefined
        });
    }

    private async _getUnitConversions(): Promise<void> {
        const { actionContext, request, app } = this.props.context;

        if (!request.user.isAuthenticated || app.config.unitOfMeasureConversions !== true) {
            return;
        }

        const id = (await this.props.data.product).RecordId;

        if (!id) {
            return;
        }

        const unitConversions = await getProductUnitOfMeasureOptionsAsync({ callerContext: actionContext }, [ id ]);

        this.setState({ unitConversions });
    }

    private _getProductVariant(dimensionsToUpdate: { [id: number]: string }) {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(
                product!.MasterProductId ? product!.MasterProductId : product!.RecordId,
                channelId,
                undefined,
                undefined,
                this.props.context.request
            ),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        return { productVariantPromise: selectedProductPromise, mappedDimensions };
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const product = this.props.data.product.result;

        const productDimensions = this.props.data.productDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };

        this.setState({ isUpdatingDimension: true, isUpdatingDeliveryOptions: true });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[key]) {
                this.setState(previousState => {
                    const errorState = previousState.errorState;
                    errorState.configureErrors[key] = undefined;
                    return { errorState };
                });
            }
        }

        const data = this._getProductVariant(dimensionsToUpdate);
        const productVariant: SimpleProduct | null = await data.productVariantPromise;

        if (!productVariant) {
            return;
        }

        const promises: Promise<void>[] = [
            this._getDimensionsForSelectedVariant(productVariant, data.mappedDimensions),
            this._hydrateInventoryInfo(productVariant),
            this._hydratePrice(productVariant),
            this._hydrateDeliveryOptions(productVariant),
            this._updateQuantitiesInState(productVariant)
        ];

        await Promise.all(promises);
    };

    private async _getDimensionsForSelectedVariant(
        productVariant: SimpleProduct,
        mappedDimensions: {
            /* eslint-disable @typescript-eslint/naming-convention -- Retail proxy values */
            DimensionTypeValue: number;
            DimensionValue: ProductDimensionValue | undefined;
            ExtensionProperties: CommerceProperty[] | undefined;
            /* eslint-enable @typescript-eslint/naming-convention -- Retail proxy values */
        }[]
    ): Promise<void> {
        await getDimensionsForSelectedVariant(
            new GetDimensionsForSelectedVariantInput(
                productVariant.MasterProductId ? productVariant.MasterProductId : productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                mappedDimensions,
                this.props.context.request
            ),
            this.props.context.actionContext
        );
    }

    private async _hydrateInventoryInfo(productVariant: SimpleProduct): Promise<void> {
        // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
        // so that places like add to cart can await it
        const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                productVariant
            ),
            this.props.context.actionContext
        );
        const newShippingQuantity = newAvailableQuantity?.find(shipping => shipping.deliveryType === DeliveryMode.shipping);
        const isCustomPriceSelected = productVariant.Dimensions?.find(
            dimension =>
                dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value?.toLowerCase() === 'custom'
        );
        if (isCustomPriceSelected) {
            this.setState({ isCustomPriceSelected: true });
        } else {
            this.setState(previousState => {
                const errorState = previousState.errorState;
                errorState.customAmountError = undefined;
                return { isCustomPriceSelected: false, isPriceKeyedIn: false, errorState };
            });
        }

        if (newShippingQuantity) {
            this.setState({ productAvailableQuantity: newShippingQuantity });
        } else {
            this.setState({ productAvailableQuantity: undefined });
        }
    }

    private async _hydratePrice(productVariant: SimpleProduct): Promise<void> {
        // This function is invoked when dimensionSelectedAsync get called.
        // If the price has not been hydrated before, this means the component was just loaded for the first time with variant selected.
        // We will skip calling RS API in this case as the price data has already been fetched via module data action in node server.
        if (!this.state.isPriceApiCalled) {
            this.setState({ isPriceApiCalled: true });
            return;
        }

        // Step 6. Use these dimensions hydrate the product price.
        const newPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId, productVariant),
            this.props.context.actionContext
        );

        if (newPrice) {
            this._updatePrice(newPrice);
        }
    }

    private async _hydrateDeliveryOptions(productVariant: SimpleProduct): Promise<void> {
        const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );

        // Step 7. Use these dimensions hydrate the product delivery options.
        const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                productVariant,
                undefined,
                retailMultiplePickupFeatureState?.IsEnabled
            ),
            this.props.context.actionContext
        );

        if (newDeliveryOptions) {
            this.setState({ productDeliveryOptions: newDeliveryOptions });
        }
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
        const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
        const isChannelLimitsFeatureEnabled = await this._isChannelLimitsFeatureEnabled();

        const min: number = this._getMinQuantityForCartLineItem(isOrderQuantityLimitsFeatureEnabled, product);
        const max: number = this._getMaxQuantityForCartLineItem(
            isChannelLimitsFeatureEnabled || isOrderQuantityLimitsFeatureEnabled,
            product
        );
        let quantity: number = this._getQuantity(product, isOrderQuantityLimitsFeatureEnabled);
        quantity = Math.max(quantity, min);

        this.setState({ min, max, quantity });
    }

    private _getMinQuantityForCartLineItem(isOrderQuantityLimitsFeatureEnabled: boolean, product: SimpleProduct): number {
        if (
            isOrderQuantityLimitsFeatureEnabled &&
            this._getIgnoreOrderSettings() &&
            product?.Behavior?.MinimumQuantity &&
            product.Behavior.MinimumQuantity > 0
        ) {
            return product.Behavior.MinimumQuantity;
        }

        if (product?.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 0) {
            return product.Behavior.MultipleOfQuantity;
        }

        return 0;
    }

    private _getMaxQuantityForCartLineItem(isQuantityLimitsFeatureEnabled: boolean, product: SimpleProduct): number {
        if (
            isQuantityLimitsFeatureEnabled &&
            this._getIgnoreOrderSettings() &&
            product?.Behavior?.MaximumQuantity &&
            product.Behavior.MaximumQuantity > 0
        ) {
            return product.Behavior.MaximumQuantity;
        }

        return updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem);
    }

    /**
     * Retrieves the value of the IgnoreOrderSettings property from the cart configuration.
     * If the property is not present, it defaults to false.
     * @returns {boolean} The value of the IgnoreOrderSettings property.
     */
    private _getIgnoreOrderSettings(): boolean {
        const cartConfiguration = this.props.data.cartConfiguration;
        return !(cartConfiguration?.result?.IgnoreOrderSettings ?? false);
    }

    private _getQuantity(product: SimpleProduct, isOrderQuantityLimitsFeatureEnabled: boolean): number {
        const { quantity } = this.state;
        let defaultQuantity = 1;

        if (quantity !== defaultQuantity || !isOrderQuantityLimitsFeatureEnabled) {
            return quantity;
        }

        const cartConfiguration = this.props.data.cartConfiguration;
        const applyDefaultOrderSettingsForCartLine = cartConfiguration?.result?.ValidateDefaultOrderSettingsPerLine ?? false;
        if (!applyDefaultOrderSettingsForCartLine) {
            return quantity;
        }

        if (product?.Behavior?.DefaultQuantity && product.Behavior.DefaultQuantity > 0) {
            defaultQuantity = product.Behavior.DefaultQuantity;
        }

        return defaultQuantity;
    }

    private readonly _getDropdownName = (dimensionType: number, resources: IVinhusetBuyboxResources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const featureStatuses = await this.props.data.featureState;

        const isFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
        )?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }
        const useSiteBuilderSettings = featureStatuses?.find(
            featureState => featureState.Name === RevertToSiteBuilderOrderQuantityLimitsSettingsCRTFeatureName
        )?.IsEnabled;

        if (useSiteBuilderSettings) {
            const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
            if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
                return false;
            }

            if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
                return true;
            }
            let customerInfo;
            try {
                customerInfo = await this.props.data.customerInformation;
            } catch (error) {
                this.props.telemetry.information(error);
                this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
                return false;
            }

            return (
                customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        } else {
            const cartConfiguration = await this.props.data.cartConfiguration;
            return !(cartConfiguration?.IgnoreOrderSettings ?? false);
        }
    }

    private _validateCatalogDetails(catalogId: number): ProductCatalog[] | undefined {
        const catalogs = this.props.data.catalogs?.result?.filter(item => item.RecordId === catalogId);
        if (ArrayExtensions.hasElements(catalogs) && catalogs[0].RecordId === 0) {
            return;
        }

        return catalogs;
    }

    private async _isChannelLimitsFeatureEnabled(): Promise<boolean> {
        const featureStatuses = await this.props.data.featureState;

        const isChannelLimitsFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === EcommerceQuantityLimitConfigurationsFeatureName
        )?.IsEnabled;

        return isChannelLimitsFeatureEnabledInHq ?? false;
    }

    private _renderProductCategory(): React.ReactNode {
        const { categoryPaths } = this.props.data;
        const category = categoryPaths.result?.[0];

        if (!category || !category.Name) {
            return null;
        }

        return (
            <p className='ms-buybox__product-category'>
                <a href={category?.Url}>{category?.Name}</a>
            </p>
        );
    }

    private _renderProductProducer(): React.ReactNode {
        const { data: { productSpecificationData } } = this.props;

        const producer = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.producer, undefined);

        if (!producer) {
            return null;
        }

        return (
            <div className='ms-buybox__product-producer'>
                <a href='#'>{producer}</a>
            </div>
        );
    }

    private _renderProductDescription(): React.ReactNode {
        const { data: { productSpecificationData } } = this.props;

        const description = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.description, undefined);

        if (!description) {
            return null;
        }

        return (
            <p className={`ms-buybox__product-description`}>{description}</p>
        );
    }

    private _renderProductRegion(): React.ReactNode {
        const { productSpecificationData } = this.props.data;

        if (!productSpecificationData.result) {
            return null;
        }

        const country = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.country, undefined);
        const district = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.district, undefined);
        const underDistrict = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.underDistrict, undefined);
        const combined = [country, district, underDistrict].filter(Boolean) as string[];

        if (combined.length === 0) {
            return null;
        }

        return (
            <div className='ms-buybox__product-region'>
                {combined.map((item, index) => {
                    let searchURL = getUrlSync('search', this.props.context.actionContext);
                    const separator = searchURL!.includes('?') ? '&' : '?';
                    searchURL = `${searchURL}${separator}q=${encodeURIComponent(item)}`;

                    return (
                        <>
                            <a key={index} href={searchURL}>{item}</a>
                            {index < combined.length - 1 && <span>, </span>}
                        </>
                    );
                })}
            </div>
        );
    }

    private _renderLiterPrice(): React.ReactNode {
        const { context, resources } = this.props;
        const { product, productPrice, productSpecificationData } = this.props.data;
        const { unitConversions } = this.state;

        if (!productPrice.result || !productSpecificationData.result) {
            return null;
        }

        const fpakToCl = unitConversions?.find(x => x.FromUnitOfMeasureSymbol === product.result?.DefaultUnitOfMeasure && x.ToUnitOfMeasureSymbol === 'cl')?.Factor;
        const fpakToLtr = unitConversions?.find(x => x.FromUnitOfMeasureSymbol === product.result?.DefaultUnitOfMeasure && x.ToUnitOfMeasureSymbol === 'ltr')?.Factor;
        const volumeClFromAttr = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.volumeCl, undefined);
        const volumeCl = (volumeClFromAttr ? parseFloat(volumeClFromAttr) : undefined) || fpakToCl || (fpakToLtr ? fpakToLtr * 100 : undefined);

        if (!volumeCl) {
            return null;
        }

        const literPrice = (productPrice.result.CustomerContextualPrice || 0) / (volumeCl / 100);

        return (
            <div className='ms-buybox__liter-price__wrapper'>
                <span className='ms-buybox__liter-price__price'>
                    {context.cultureFormatter.formatCurrency(literPrice)}
                    &nbsp;
                    {format(resources.pricePerText, 'l')}
                </span>
            </div>
        );
    }

    private _renderDepositPrice(): React.ReactNode {
        const {
            resources,
            data: { product }
        } = this.props;

        const deposit = product.result?.ExtensionProperties?.find(x => x.Key === 'DEPOSIT')?.Value?.DecimalValue;

        if (!deposit) {
            return null;
        }

        return (
            <div className='ms-buybox__deposit-price__wrapper'>
                <span className='ms-buybox__deposit-price__price'>
                    + {this.props.context.cultureFormatter.formatCurrency(deposit)} {resources.depositSuffixText}
                </span>
            </div>
        );
    }

    private _renderVolumeCl(): React.ReactNode {
        const { product, productPrice, productSpecificationData } = this.props.data;
        const { unitConversions } = this.state;

        if (!productPrice.result || !productSpecificationData.result) {
            return null;
        }

        const fpakToCl = unitConversions?.find(x => x.FromUnitOfMeasureSymbol === product.result?.DefaultUnitOfMeasure && x.ToUnitOfMeasureSymbol === 'cl')?.Factor;
        const fpakToLtr = unitConversions?.find(x => x.FromUnitOfMeasureSymbol === product.result?.DefaultUnitOfMeasure && x.ToUnitOfMeasureSymbol === 'ltr')?.Factor;
        const volumeClFromAttr = getAttributeStringOrDefault(productSpecificationData.result, this.attributeLocale.volumeCl, undefined);
        const volumeCl = (volumeClFromAttr ? parseFloat(volumeClFromAttr) : undefined) || fpakToCl || (fpakToLtr ? fpakToLtr * 100 : undefined);

        return (
            <div className='msc-buybox__volume'>{volumeCl} cl</div>
        );
    }

    private _renderPricePer(): React.ReactNode {
        const {
            context,
            resources,
            data: { product, productPrice }
        } = this.props;

        if (!product.result || !productPrice.result || !product.result.Behavior?.DefaultQuantity || this.state.breakoff) {
            return null;
        }

        const uom = humanizeUnitOfMeasure(context, product.result.DefaultUnitOfMeasure, product.result).toLocaleLowerCase();

        return (
            <div>
                <span>
                    {context.cultureFormatter.formatCurrency(productPrice.result.CustomerContextualPrice || 0)}
                    &nbsp;
                    {format(resources.pricePerText || 'pr. {0}', uom)}
                </span>
            </div>
        );
    }
}

export default Buybox;
